import { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Command, CommandGroup, CommandItem } from "@/components/ui/command";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { captureError } from '@/lib/sentry';

interface ContactBasicInfoProps {
  contact: {
    id: string;
    first_name: string;
    last_name: string;
    industry?: string;
    company_name?: string;
    job_title?: string;
  };
  onUpdate: () => void;
}

export function ContactBasicInfo({ contact, onUpdate }: ContactBasicInfoProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const [values, setValues] = useState({
    first_name: contact.first_name,
    last_name: contact.last_name,
    industry: contact.industry || '',
    company_name: contact.company_name || '',
    job_title: contact.job_title || '',
  });
  const [suggestions, setSuggestions] = useState<{ companies: string[]; industries: string[] }>({
    companies: [],
    industries: [],
  });
  const [showSuggestions, setShowSuggestions] = useState<'company' | 'industry' | null>(null);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const justSelectedSuggestion = useRef(false);
  const debugTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debugTimeoutRef.current) {
        window.clearTimeout(debugTimeoutRef.current);
      }
    };
  }, []);

  const fetchSuggestions = async (field: 'company_name' | 'industry', value: string) => {
    if (!currentWorkspace || !value.trim()) {
      setSuggestions(prev => ({ ...prev, [field === 'company_name' ? 'companies' : 'industries']: [] }));
      return;
    }

    try {
      console.log(`Fetching ${field} suggestions for "${value}"`);
      
      const { data, error } = await supabase
        .from('contacts')
        .select(field)
        .eq('workspace_id', currentWorkspace.id)
        .not(field, 'is', null)
        .ilike(field, `%${value}%`)
        .order(field)
        .limit(5);

      if (error) throw error;

      const uniqueValues = Array.from(new Set(data.map(d => d[field as keyof typeof d])));
      console.log(`Got ${uniqueValues.length} suggestions for ${field}:`, uniqueValues);
      
      setSuggestions(prev => ({
        ...prev,
        [field === 'company_name' ? 'companies' : 'industries']: uniqueValues,
      }));
    } catch (error) {
      console.error(`Error fetching ${field} suggestions:`, error);
    }
  };

  const handleUpdate = async (field: string, explicitValue?: string) => {
    try {
      // Use explicitly passed value or get from state
      const value = explicitValue !== undefined ? explicitValue.trim() : values[field as keyof typeof values].trim();
      
      console.log(`handleUpdate: Saving ${field} = "${value}" (explicitValue: ${explicitValue !== undefined ? `"${explicitValue}"` : 'undefined'})`);

      const { error } = await supabase
        .from('contacts')
        .update({ [field]: value })
        .eq('id', contact.id);

      if (error) throw error;

      console.log(`Successfully saved ${field} = "${value}"`);
      
      toast({
        description: "Contact updated successfully",
      });
      
      onUpdate();
      setEditing(null);
      setShowSuggestions(null);
    } catch (error) {
      console.error('Error updating contact:', error);
      captureError(error as Error, { feature: 'contacts-contact-basic-info-handle-update' });
      toast({
        title: "Error",
        description: "Failed to update contact",
        variant: "destructive",
      });
    } finally {
      // Reset selected suggestion flag AFTER save completes to avoid race conditions
      console.log(`handleUpdate: Resetting justSelectedSuggestion to false`);
      justSelectedSuggestion.current = false;
    }
  };

  const handleSuggestionSelect = (field: 'company_name' | 'industry', suggestion: string) => {
    console.log(`handleSuggestionSelect: Selected "${suggestion}" for ${field}`);
    
    // Set flag BEFORE any async operations to prevent race conditions
    justSelectedSuggestion.current = true;
    console.log(`Set justSelectedSuggestion to true`);
    
    // Update local state for UI display
    setValues(prev => ({ ...prev, [field]: suggestion }));
    setShowSuggestions(null);
    
    // Debug: Check state AFTER update but BEFORE it's reflected (async)
    console.log(`Values immediately after setValues (unlikely to be updated yet):`, values);
    
    // Add a debug timeout to see if state is updated after a delay
    debugTimeoutRef.current = window.setTimeout(() => {
      console.log(`Values after delay:`, values);
    }, 100);
    
    // Save directly with the suggestion value instead of relying on state
    // This is the key fix - we pass the suggestion directly rather than reading from state
    handleUpdate(field, suggestion);
  };

  const renderField = (
    label: string, 
    field: keyof typeof values, 
    placeholder: string,
    className?: string
  ) => (
    <div className={cn("space-y-1", className)}>
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {editing === field ? (
        <div className="relative">
          <Input
            ref={field === 'company_name' || field === 'industry' ? inputRef : undefined}
            value={values[field]}
            onChange={(e) => {
              const value = e.target.value;
              setValues(prev => ({ ...prev, [field]: value }));
              if (field === 'company_name' || field === 'industry') {
                fetchSuggestions(field, value);
                setShowSuggestions(field === 'company_name' ? 'company' : 'industry');
              }
            }}
            placeholder={placeholder}
            className="h-7 text-xs"
            onKeyDown={(e) => {
              console.log(`onKeyDown: key=${e.key}, field=${field}, justSelectedSuggestion=${justSelectedSuggestion.current}`);
              if (e.key === 'Enter') {
                handleUpdate(field);
              } else if (e.key === 'Escape') {
                setEditing(null);
                setValues(prev => ({ ...prev, [field]: contact[field] || '' }));
                setShowSuggestions(null);
              }
            }}
            onBlur={() => {
              console.log(`onBlur: field=${field}, values[field]="${values[field]}", justSelectedSuggestion=${justSelectedSuggestion.current}`);
              
              // If a suggestion was just selected, don't save again on blur
              if (editing === field && !justSelectedSuggestion.current) {
                console.log(`Triggering handleUpdate from onBlur for ${field}`);
                handleUpdate(field);
              } else if (justSelectedSuggestion.current) {
                console.log(`Skipping handleUpdate on blur because suggestion was just selected`);
              }
            }}
            onFocus={() => {
              if (field === 'company_name' || field === 'industry') {
                fetchSuggestions(field, values[field]);
                setShowSuggestions(field === 'company_name' ? 'company' : 'industry');
              }
            }}
            autoFocus
          />
          {showSuggestions && 
           ((showSuggestions === 'company' && field === 'company_name' && suggestions.companies.length > 0) ||
            (showSuggestions === 'industry' && field === 'industry' && suggestions.industries.length > 0)) && (
            <div ref={suggestionsRef}>
              <Command className="absolute z-50 w-full mt-1">
                <CommandGroup>
                  {(showSuggestions === 'company' ? suggestions.companies : suggestions.industries).map((suggestion) => (
                    <CommandItem
                      key={suggestion}
                      onSelect={() => {
                        handleSuggestionSelect(
                          field as 'company_name' | 'industry', 
                          suggestion
                        );
                      }}
                      className="text-xs"
                    >
                      {suggestion}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </Command>
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "px-2 py-1 rounded-md cursor-text hover:bg-accent/50 transition-colors",
            "text-xs min-h-[1.75rem] flex items-center"
          )}
          onClick={() => setEditing(field)}
        >
          {values[field] || <span className="text-muted-foreground">{placeholder}</span>}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {renderField('First Name', 'first_name', 'Enter first name')}
        {renderField('Last Name', 'last_name', 'Enter last name')}
      </div>
      
      {renderField('Company', 'company_name', 'Enter company name')}
      
      <div className="grid grid-cols-2 gap-3">
        {renderField('Industry', 'industry', 'Enter industry')}
        {renderField('Job Title', 'job_title', 'Enter job title')}
      </div>
    </div>
  );
}